import { Laptop, Smartphone, Tablet, Monitor, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { useHandoff } from "../../contexts/HandoffContext";

const iconFor = (deviceType) => {
  if (deviceType === "mobile") return Smartphone;
  if (deviceType === "tablet") return Tablet;
  if (deviceType === "desktop") return Monitor;
  return Laptop;
};

// Picker opened from the profile menu's "Switch" action — lists the caller's
// OTHER logged-in sessions (System > Users > Devices rows) that are currently
// online and sends the current page to whichever one is picked. The backend
// (GET /handoff/targets) already filters to sessions active within the last
// 5 minutes, so everything here is online by construction — an offline device
// would just sit unanswered until the request expires, so it's never listed.
// Sized to fit a small phone screen without spilling past its edges: a tight
// width cap plus compact padding/type scale below the sm breakpoint.
const HandoffSwitchDialog = () => {
  const { pickerOpen, closePicker, targets, targetsLoading, sendSwitch } = useHandoff();

  return (
    <Dialog open={pickerOpen} onOpenChange={(open) => !open && closePicker()}>
      <DialogContent className="max-w-[280px] sm:max-w-sm w-[85vw] sm:w-full max-h-[70vh] sm:max-h-[85vh] flex flex-col p-3 sm:p-4 gap-1.5 sm:gap-2">
        <DialogHeader className="space-y-0.5 !w-full !mx-0">
          <DialogTitle className="text-sm sm:text-base pr-4">Switch to another device</DialogTitle>
          <DialogDescription className="text-[11px] sm:text-sm break-words whitespace-normal leading-snug">
            Send this page to one of your other online devices.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1 sm:gap-1.5 py-0.5 overflow-y-auto -mx-1 px-1 !w-full !max-w-full !-mx-1">
          {targetsLoading && (
            <p className="text-xs sm:text-sm text-muted-foreground px-1 py-3 text-center">Loading…</p>
          )}
          {!targetsLoading && targets.length === 0 && (
            <p className="text-xs sm:text-sm text-muted-foreground px-1 py-3 text-center">
              No other devices online.
            </p>
          )}
          {targets.map((t) => {
            const Icon = iconFor(t.device_type);
            return (
              <button
                key={t.session_id}
                type="button"
                onClick={() => sendSwitch(t)}
                className="flex items-center gap-2 sm:gap-3 rounded-md border border-border px-2 py-1.5 sm:px-3 sm:py-2 text-left hover:bg-accent active:bg-accent transition-colors"
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm font-medium truncate">{t.device_label || "Unknown device"}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Online now</p>
                </div>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-green-500" aria-hidden="true" />
                <Send className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HandoffSwitchDialog;
